/**
 * Chain Configuration
 * Maps chain IDs to viem chain definitions
 */

import {
  sepolia,
  baseSepolia,
  arbitrumSepolia,
  bscTestnet,
  mainnet,
  base,
  polygon,
  arbitrum,
  bsc,
} from 'wagmi/chains';
import type { Chain as ViemChain } from 'viem';
import { robinhood, robinhoodTestnet } from 'viem/chains';

import type { Chain } from 'types';

import { polygonAmoyFixed } from './wagmi';

/**
 * Mapping of chain IDs to viem chain objects
 * This maps the API's chain IDs to the corresponding viem chain definitions
 */
export const VIEM_CHAIN_MAP: Record<number, ViemChain> = {
  // Testnets
  11155111: sepolia,
  84532: baseSepolia,
  // Fixed definition: viem's default Amoy RPC has dead DNS (see wagmi.ts)
  80002: polygonAmoyFixed,
  421614: arbitrumSepolia,
  97: bscTestnet,
  46630: robinhoodTestnet,

  // Mainnets (for future use)
  1: mainnet,
  8453: base,
  137: polygon,
  42161: arbitrum,
  56: bsc,
  4663: robinhood,
};

/**
 * Chain name to chain ID mapping
 */
export const CHAIN_NAME_TO_ID: Record<string, number> = {
  // Testnets
  'sepolia': 11155111,
  'base-sepolia': 84532,
  'polygon-amoy': 80002,
  'arbitrum-sepolia': 421614,
  'bsc-testnet': 97,
  'robinhood-testnet': 46630,

  // Mainnets
  'mainnet': 1,
  'base': 8453,
  'polygon': 137,
  'arbitrum': 42161,
  'bsc': 56,
  'robinhood': 4663,
};

/**
 * Chain ID to name mapping (reverse of above)
 */
export const CHAIN_ID_TO_NAME: Record<number, string> = Object.entries(CHAIN_NAME_TO_ID)
  .reduce((acc, [name, id]) => {
    acc[id] = name;

    return acc;
  }, {} as Record<number, string>);

/**
 * Default chain (Sepolia) for fallback
 */
export const DEFAULT_CHAIN_ID = 11155111;
export const DEFAULT_CHAIN_NAME = 'sepolia';

/**
 * MultiSigLocker contract addresses per chain — FALLBACK ONLY.
 *
 * Source of truth is the API: GET /chains returns `lockerAddress` from the
 * `chains` table. This map is used only when the API is not configured
 * (static mode) or an older API omits the field. Keep it in sync when
 * deploying new lockers, but the DB row is what production reads.
 */
export const LOCKER_ADDRESSES: Record<number, string> = {
  // Testnets
  // make-dev fleet (CREATE2: same proxy on every chain), matching public/config.js
  11155111: '0xe9Dc2E30A9A03835cAe2466C01269218baeEC9fa', // Sepolia
  84532: '0xe9Dc2E30A9A03835cAe2466C01269218baeEC9fa', // Base Sepolia
  80002: '0xe9Dc2E30A9A03835cAe2466C01269218baeEC9fa', // Polygon Amoy
  421614: '0x0000000000000000000000000000000000000000', // Arbitrum Sepolia (no live fleet)
  97: '0x0000000000000000000000000000000000000000', // BSC Testnet (no live fleet)
  46630: '0xe9Dc2E30A9A03835cAe2466C01269218baeEC9fa', // Robinhood Chain Testnet

  // Mainnets (TBD - placeholders)
  1: '0x0000000000000000000000000000000000000000', // Ethereum
  8453: '0x0000000000000000000000000000000000000000', // Base
  137: '0x0000000000000000000000000000000000000000', // Polygon
  42161: '0x0000000000000000000000000000000000000000', // Arbitrum
  56: '0x0000000000000000000000000000000000000000', // BSC
};

/**
 * Get locker address for a chain
 */
export function getLockerAddress(chainId: number): string {
  return LOCKER_ADDRESSES[chainId] || '0x0000000000000000000000000000000000000000';
}

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/**
 * Static routes are usable only when a real locker is deployed.
 */
export function isStaticChainEnabled(chainId: number): boolean {
  return getLockerAddress(chainId) !== ZERO_ADDRESS;
}

/**
 * Get the viem chain for a given chain ID
 */
export function getViemChain(chainId: number): ViemChain | undefined {
  return VIEM_CHAIN_MAP[chainId];
}

/**
 * Get the viem chain for a given chain name
 */
export function getViemChainByName(chainName: string): ViemChain | undefined {
  const chainId = CHAIN_NAME_TO_ID[chainName.toLowerCase()];

  return chainId ? VIEM_CHAIN_MAP[chainId] : undefined;
}

/**
 * Get chain name from chain ID
 */
export function getChainName(chainId: number): string | undefined {
  return CHAIN_ID_TO_NAME[chainId];
}

/**
 * Get chain ID from chain name
 */
export function getChainId(chainName: string): number | undefined {
  return CHAIN_NAME_TO_ID[chainName.toLowerCase()];
}

/**
 * Check if a chain ID is a testnet
 */
export function isTestnet(chainId: number): boolean {
  const testnetIds = [11155111, 84532, 80002, 421614, 97, 46630];

  return testnetIds.includes(chainId);
}

/**
 * Get chain display info (short name, badge, etc.)
 */
export function getChainDisplayInfo(chainId: number): {
  shortName: string;
  color: string;
  isTestnet: boolean;
} {
  const info: Record<number, { shortName: string; color: string; isTestnet: boolean }> = {
    // Testnets
    // Display label matches the API's `name` ("Ethereum Sepolia") so the
    // first-paint static fallback doesn't flash a different label ("Sepolia")
    // than the API-backed dropdown.
    11155111: { shortName: 'Ethereum Sepolia', color: '#627EEA', isTestnet: true },
    84532: { shortName: 'Base Sepolia', color: '#0052FF', isTestnet: true },
    80002: { shortName: 'Polygon Amoy', color: '#8247E5', isTestnet: true },
    421614: { shortName: 'Arbitrum Sepolia', color: '#12AAFF', isTestnet: true },
    97: { shortName: 'BSC Testnet', color: '#F0B90B', isTestnet: true },
    46630: { shortName: 'Robinhood Testnet', color: '#00C805', isTestnet: true },

    // Mainnets
    1: { shortName: 'Ethereum', color: '#627EEA', isTestnet: false },
    8453: { shortName: 'Base', color: '#0052FF', isTestnet: false },
    137: { shortName: 'Polygon', color: '#8247E5', isTestnet: false },
    42161: { shortName: 'Arbitrum', color: '#12AAFF', isTestnet: false },
    56: { shortName: 'BSC', color: '#F0B90B', isTestnet: false },
    4663: { shortName: 'Robinhood', color: '#00C805', isTestnet: false },
  };

  return info[chainId] || { shortName: 'Unknown', color: '#888888', isTestnet: false };
}

/**
 * Convert API Chain to array of viem chains
 */
export function apiChainsToViemChains(apiChains: Chain[]): ViemChain[] {
  return apiChains
    .filter(chain => chain.isEnabled)
    .map(chain => getViemChain(chain.chainId))
    .filter((chain): chain is ViemChain => chain !== undefined);
}

/**
 * LocalStorage key for persisting selected chain
 */
export const SELECTED_CHAIN_STORAGE_KEY = 'cspr-link-selected-chain';

/**
 * Save selected chain ID to localStorage
 */
export function saveSelectedChain(chainId: number): void {
  try {
    localStorage.setItem(SELECTED_CHAIN_STORAGE_KEY, String(chainId));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Load selected chain ID from localStorage
 */
export function loadSelectedChain(): number | null {
  try {
    const stored = localStorage.getItem(SELECTED_CHAIN_STORAGE_KEY);
    if (stored) {
      const chainId = parseInt(stored, 10);
      if (!isNaN(chainId)) {
        return chainId;
      }
    }
  } catch {
    // Ignore localStorage errors
  }

  return null;
}
